import React from 'react';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { FormSwitch, FormAsyncSelect } from '../FormInputs';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { DateRangeSettingsSubgroup } from './DateRangeSettingsSubgroup';
import { DefaultSortingSubgroup } from './DefaultSortingSubgroup';
import { WidgetsSettingsSubgroup } from './WidgetsSettingsSubgroup';
import Switch from '../../components/Switch';
import styles from '../../pages/NarrativeEditPage/NarrativeEditPage.module.scss';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';

interface AdditionalSettingsGroupProps {
  form: any;
  narrativeTypes: any[];
  mode?: 'add' | 'edit';
}

export const AdditionalSettingsGroup: React.FC<
  AdditionalSettingsGroupProps
> = ({ form, narrativeTypes, mode = 'add' }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const currentNarrativeTypeData =
    narrativeTypes[form.state.values.currentNarrativeType || 0];
  const isTopicModeling = currentNarrativeTypeData?.type === 'TOPIC_MODELING';

  const getWorkspaces = (
    inputValue: string,
    callback: (options: { value: string | number; label: string }[]) => void,
  ) => {
    return API.fetch('GET', `/API/v1/workspaces?q=${inputValue}`).then(
      (data) => {
        callback(
          data.objects
            .sort(
              (
                a: { id: string | number; name: string },
                b: { id: string | number; name: string },
              ) => (a.name > b.name ? 1 : -1),
            )
            .map((workspace: { id: string | number; name: string }) => {
              return { value: workspace.id, label: workspace.name };
            }),
        );
      },
    ).catch((error) => {
      showRequestError(error, t('Workspaces'));
      callback([]);
    });
  };

  let workspacesPromiseTimeout: NodeJS.Timeout | null = null;
  const workspacePromiseOptions = function (
    inputValue: string,
  ): Promise<{ value: string | number; label: string }[]> {
    if (workspacesPromiseTimeout !== null) {
      clearTimeout(workspacesPromiseTimeout);
    }

    if (!inputValue) {
      return Promise.resolve([]);
    }

    return new Promise((resolve) => {
      workspacesPromiseTimeout = setTimeout(() => {
        getWorkspaces(inputValue, resolve);
      }, 1000);
    });
  };

  return (
    <form.Subscribe
      selector={(state: any) => state.values.currentNarrativeType}
      children={(currentNarrativeType: any) => {
        const currentNarrativeTypeData =
          narrativeTypes[currentNarrativeType || 0];
        const isTopicModeling =
          currentNarrativeTypeData?.type === 'TOPIC_MODELING';
          if ((!currentUser?.is_super_admin && (!isTopicModeling && mode !== 'edit'))) {
            return null;
          }

        return (
          <div className="form-wrapper">
            <h2>
              {isTopicModeling
                ? t('Topic modeling additional settings')
                : t('Case additional settings')}
            </h2>

            {currentUser?.is_super_admin && (
              <div className="group-card">
                <FormSwitch
                  form={form}
                  name="isManuallyManaged"
                  label={t('Manually managed')}
                />
              </div>
            )}

            {mode === 'edit' && !isTopicModeling && (
              <div className="group-card">
                <div className="form-element">
                  <form.Subscribe
                    selector={(state: any) => state.values.refreshWholeCase}
                    children={(refreshWholeCase: any) => (
                      <label htmlFor="case-refresh" className={styles.label}>
                        {t('Refresh whole case history')}
                        <div className="sub-label">
                          {refreshWholeCase
                            ? t('Rebuild case from scratch using new settings.')
                            : t(
                                'Keep current content; apply settings to new publications only.',
                              )}
                        </div>
                      </label>
                    )}
                  />
                  <form.Field
                    name="refreshWholeCase"
                    children={(field: any) => (
                      <>
                        <Switch
                          id="case-refresh"
                          value={field.state.value || false}
                          onChange={(value: boolean) =>
                            field.handleChange(value)
                          }
                        />
                      </>
                    )}
                  />
                </div>
              </div>
            )}

            {mode === 'edit' && isTopicModeling && currentUser?.is_super_admin && (
              <div className="group-card">
                <FormAsyncSelect
                  form={form}
                  name="organizations"
                  label={t('Organizations')}
                  placeholder={t('Search or select organizations')}
                  loadOptions={(value) => workspacePromiseOptions(value)}
                  defaultOptions
                  isMulti
                  noOptionsMessage={() => t('Please enter at least 1 symbol')}
                  loadingMessage={() => t('Loading...')}
                />
              </div>
            )}

            {isTopicModeling && (
              <>
                <div className="group-card">
                  <div className="group-card-row">
                    <FormSwitch
                      form={form}
                      name="addDateRangeSettings"
                      label={t('Date range filter preset')}
                      subLabel={t(
                        'Define the date range that will be selected by default for topic modeling.',
                      )}
                    />

                    <form.Subscribe
                      selector={(state: any) =>
                        state.values.addDateRangeSettings
                      }
                      children={(addDateRangeSettings: any) =>
                        addDateRangeSettings && (
                          <DateRangeSettingsSubgroup form={form} />
                        )
                      }
                    />
                  </div>
                </div>

                <div className="group-card">
                  <div className="group-card-row">
                    <FormSwitch
                      form={form}
                      name="addDefaultSorting"
                      label={t('Add default sorting')}
                      subLabel={t(
                        'Define the default sorting applied to topic modeling results.',
                      )}
                    />

                    <form.Subscribe
                      selector={(state: any) => state.values.addDefaultSorting}
                      children={(addDefaultSorting: any) =>
                        addDefaultSorting && (
                          <DefaultSortingSubgroup form={form} />
                        )
                      }
                    />
                  </div>
                </div>

                <div className="group-card">
                  <div className="group-card-row">
                    <FormSwitch
                      form={form}
                      name="widgetsSettings"
                      label={t('Widgets settings')}
                      subLabel={t(
                        'Define the widget configuration for topic modeling.',
                      )}
                    />

                    <form.Subscribe
                      selector={(state: any) => state.values.widgetsSettings}
                      children={(widgetsSettings: any) =>
                        widgetsSettings && (
                          <WidgetsSettingsSubgroup form={form} />
                        )
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }}
    />
  );
};
